import { atom } from 'jotai';

import { AgentStatusEnum, AppRouteEnum } from '@lib/enums';
import { encodeCallSimulation } from '@lib/utils/encodeCallSimulation';
import { getAppUrl } from '@lib/utils/getAppUrl';
import { softphoneAtom } from '@states/softphone/softphoneAtom';

import type { ChannelEnum, CallDirectionEnum } from '@lib/enums';
import type { TSoftphoneState } from '@states/softphone/softphoneAtom';

export type TContact = Readonly<{
  isActiveContact: boolean;
  isSoftphoneConnected: boolean;
  callDirection: Nullable<CallDirectionEnum>;
  channel: Nullable<ChannelEnum>;
  contactId: Nullable<string>;
  contactLink: Nullable<string>;
  contactMasterId: Nullable<string>;
  contactPhoneNumber: Nullable<string>;
  isDialerReturn: Nullable<boolean>;
  isInternetResponder: Nullable<boolean>;
  isTvResponder: Nullable<boolean>;
  isSpanishSkillActive: Nullable<boolean>;
  isTransfer: Nullable<boolean>;
  isVoicemail: Nullable<boolean>;
  leadId: Nullable<string>;
  previousContactId: Nullable<string>;
  sessionId: Nullable<string>;
  transferConsumerId: Nullable<string>;
  transferQuoteId: Nullable<string>;
  transferContractId: Nullable<number>;
  requestedOdometer: Nullable<number>;
}>;

const contactSelector = atom<TContact>((get) => {
  const softphoneState = get(softphoneAtom);

  const isSoftphoneConnected =
    softphoneState.agentStatus !== AgentStatusEnum.Disconnected &&
    softphoneState.agentStatus !== AgentStatusEnum.Connecting;

  if (
    softphoneState.agentStatus == null ||
    softphoneState.agentStatus === AgentStatusEnum.Available ||
    softphoneState.agentStatus === AgentStatusEnum.Unavailable ||
    softphoneState.contactId == null
  ) {
    return {
      callDirection: null,
      channel: null,
      contactId: null,
      contactLink: null,
      contactMasterId: null,
      contactPhoneNumber: null,
      leadId: null,
      isActiveContact: false,
      isDialerReturn: null,
      isInternetResponder: null,
      isTvResponder: null,
      isSoftphoneConnected,
      isSpanishSkillActive: false,
      isTransfer: null,
      isVoicemail: null,
      previousContactId: softphoneState.contactId,
      sessionId: null,
      transferConsumerId: null,
      transferQuoteId: null,
      transferContractId: null,
      requestedOdometer: null,
    };
  }

  return {
    isSoftphoneConnected,
    callDirection: softphoneState.callDirection,
    channel: softphoneState.channel,
    contactId: softphoneState.contactId,
    contactLink: buildContactLink(softphoneState),
    contactMasterId: softphoneState.contactMasterId,
    contactPhoneNumber: softphoneState.contactPhoneNumber,
    isActiveContact: true,
    isDialerReturn: softphoneState.isDialerReturn,
    isInternetResponder: softphoneState.isInternetResponder,
    isTvResponder: softphoneState.isTvResponder,
    isSpanishSkillActive: softphoneState.isSpanishSkillActive,
    isTransfer: softphoneState.isTransfer,
    isVoicemail: softphoneState.isVoicemail,
    leadId: softphoneState.leadId,
    previousContactId: null,
    sessionId: softphoneState.sessionId,
    transferConsumerId: softphoneState.transferConsumerId,
    transferQuoteId: softphoneState.transferQuoteId,
    transferContractId: softphoneState.transferContractId,
    requestedOdometer: softphoneState.requestedOdometer,
  };
});

export { contactSelector };

function buildContactLink(softphoneState: TSoftphoneState): Nullable<string> {
  if (
    softphoneState.channel == null ||
    softphoneState.contactPhoneNumber == null ||
    softphoneState.callDirection == null
  ) {
    return null;
  }

  const encodedCallSimulation = encodeCallSimulation({
    DEV_channel: softphoneState.channel,
    DEV_contactId: softphoneState.contactId,
    DEV_contactPhoneNumber: softphoneState.contactPhoneNumber,
    DEV_callDirection: softphoneState.callDirection,
    DEV_leadId: softphoneState.leadId,
    DEV_transferQuoteId: softphoneState.transferQuoteId,
    DEV_transferContractId: softphoneState.transferContractId,
    DEV_isDialerReturn: softphoneState.isDialerReturn,
    DEV_isInternetResponder: softphoneState.isInternetResponder,
    DEV_isTvResponder: softphoneState.isTvResponder,
    DEV_isVoicemail: softphoneState.isVoicemail,
    DEV_isSpanishSkillActive: softphoneState.isSpanishSkillActive,
    DEV_isTransfer: softphoneState.isTransfer,
  });

  if (encodedCallSimulation == null) {
    return null;
  }

  return `${getAppUrl()}${AppRouteEnum.ContactLink}/${encodedCallSimulation}`;
}
